import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Menu } from '@mui/material';
import {
  defaultSort,
  getIsControllerDisabled,
  getMaxSelections,
  getMinSelections,
} from './utils';
import { CustomSelectProps } from './types';
import List from '../Select/List';
import Controller from '../Select/Controller';
import { Option } from '../Select';
import { SELECT_CONTROLLER_HEIGHT } from '../Select/types';

const CustomSelect: React.FC<CustomSelectProps> = ({
  open = false,
  type,
  label,
  margin,
  min = 0,
  options,
  refElement,
  popperPosition,
  style = {},
  selectedOptions,
  menuHeight = 250,
  max = options.length,
  isHideSelectAll = false,
  isSearchable = false,
  disablePortal = false,
  closeOnSelection = false,
  isBottomControls = false,
  isWithClearSelection = false,
  menuProps,
  onClose,
  setSelectedOptions,
  sortFn = defaultSort,
}) => {
  const isAllOrOne = type === 'AllOrOne';
  const isOnlyOne = type === 'OnlyOne';

  const filterRef = useRef<HTMLDivElement>(null);

  const menuConfig = useMemo(
    () => ({
      MENU_WIDTH: 250,
      MENU_HEIGHT: menuHeight,
      MENU_TOP_HEIGHT: isSearchable ? SELECT_CONTROLLER_HEIGHT : 0,
    }),
    [menuHeight],
  );

  const sortedOptions = useMemo(
    () => (sortFn ? options.sort(sortFn) : options),
    [options, sortFn],
  );

  useEffect(() => {
    if (selectedOptions.length > max) {
      setSelectedOptions(
        (sortFn ? selectedOptions.sort(sortFn) : selectedOptions).slice(0, max),
      );
    }
  }, [sortFn]);

  const [filterValue, setFilterValue] = useState(''); // state of filter input value
  const [isOpen, setIsOpen] = useState<boolean>(open); // state of filter input value
  // WORKAROUND: fix for Virtuoso list display in transition components
  // https://github.com/petyosi/react-virtuoso/issues/542
  const [isMenuOpen, setMenuOpen] = useState(open); // state of Autocomplete input value
  const [showingOptions, setShowingOptions] = useState<Option[] | null>(null); // state of showing options
  const [lastOption, setLastOption] = useState<Option | null>(null);
  const maxSelections = getMaxSelections(min, max, options.length);
  const minSelections = getMinSelections(min, max, options.length, isAllOrOne);

  const menuStyle = (style.menu ? style.menu : {}) as any;
  const menuPaperStyle = (style.menuPaper ? style.menuPaper : {}) as any;

  const resetFiltering = useCallback(() => {
    setFilterValue('');
    setShowingOptions(sortedOptions);
    if (filterRef.current) {
      filterRef.current.focus();
    }
  }, [sortedOptions, setFilterValue, setShowingOptions]);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    resetFiltering();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // delaying the closing reset to avoid the menu reset animation on close
      setTimeout(() => {
        resetFiltering();
      }, 0);
    }
  }, [isOpen, resetFiltering, onClose]);

  // handles select|unselect all toggling
  const onSelectAllToggle = useCallback(() => {
    const maxReached = selectedOptions.length === maxSelections;
    let newSelectedOptions: Option[] = [];
    if (!maxReached) {
      const itemsToAddCount = Math.max(
        0,
        maxSelections - selectedOptions.length,
      );
      const notSelectedOptions = sortedOptions.filter(
        (item) =>
          !selectedOptions.some((option) => option.value === item.value),
      );
      const newOptions = notSelectedOptions.slice(0, itemsToAddCount);
      newSelectedOptions = [...selectedOptions, ...newOptions];
    } else if (minSelections) {
      const optionsToKeep =
        isAllOrOne && lastOption
          ? [lastOption]
          : sortedOptions.slice(0, minSelections);
      newSelectedOptions = sortedOptions.filter((option) => {
        return optionsToKeep.includes(option);
      });
    }
    setSelectedOptions(newSelectedOptions);
  }, [
    selectedOptions,
    minSelections,
    maxSelections,
    sortedOptions,
    isAllOrOne,
    lastOption,
    setSelectedOptions,
  ]);

  const onClearSelections = useCallback(() => {
    setSelectedOptions([]);
  }, [setSelectedOptions]);

  // handles item selection | unselecting
  const handleItemChange = useCallback(
    (selectedOption: Option) => {
      let newSelectedOptions: Option[] = [];

      if (isAllOrOne || isOnlyOne) {
        newSelectedOptions = sortedOptions.filter(
          (option) => option.value === selectedOption.value,
        );

        setLastOption(selectedOption);
      } else if (
        selectedOptions.some((item) => item.value === selectedOption.value)
      ) {
        const canRemove = selectedOptions.length - 1 >= minSelections;
        if (canRemove) {
          newSelectedOptions = selectedOptions.filter(
            (option) => option.value !== selectedOption.value,
          );
        }
      } else {
        const maxReached = selectedOptions.length === maxSelections;

        if (!maxReached) {
          newSelectedOptions = [...selectedOptions, selectedOption];
        } else {
          newSelectedOptions = selectedOptions;
        }
      }

      setSelectedOptions(newSelectedOptions);

      if (closeOnSelection) {
        setIsOpen(false);
      }
    },
    [
      isAllOrOne,
      isOnlyOne,
      sortedOptions,
      selectedOptions,
      setSelectedOptions,
      minSelections,
      closeOnSelection,
      setIsOpen,
    ],
  );

  // filters shown options in menu
  const onFilterValues = useCallback(
    (term: string) => {
      let filteredOptions: Option[];

      if (!term) {
        filteredOptions = [...sortedOptions];
      } else {
        filteredOptions = sortedOptions.filter(({ label, secondaryLabel }) => {
          const isLabelRelevant = label
            .toLowerCase()
            .includes(term.toLowerCase());
          const isSecondaryLabelRelevant =
            secondaryLabel?.toLowerCase().includes(term.toLowerCase()) ?? false;

          return isLabelRelevant || isSecondaryLabelRelevant;
        });
      }

      setShowingOptions(filteredOptions);
    },
    [setShowingOptions, sortedOptions],
  );

  const onSelectClose = useCallback(
    (e) => {
      e.stopPropagation();
      setIsOpen(false);
      if (onClose) onClose();
    },
    [setIsOpen, onClose],
  );

  const onMenuEntered = useCallback(() => {
    setMenuOpen(true);
  }, [setMenuOpen]);

  const onMenuExited = useCallback(
    (e) => {
      setMenuOpen(false);
    },
    [setMenuOpen, resetFiltering],
  );

  const isControllerDisabled = useMemo(
    () => getIsControllerDisabled(showingOptions, filterValue),
    [showingOptions, filterValue, getIsControllerDisabled],
  );

  const controllerMethods = useMemo(
    () => ({
      setFilterValue,
      onFilterValues,
      onSelectAllToggle,
      onClearSelections,
    }),
    [setFilterValue, onFilterValues, onSelectAllToggle],
  );

  const SelectController = useMemo(() => {
    if (!isSearchable) return null;
    return (
      <Controller
        selectedOptions={selectedOptions}
        currMax={maxSelections}
        label={label}
        filterValue={filterValue}
        filterRef={filterRef}
        isDisabled={isControllerDisabled}
        isOnlyOne={isOnlyOne}
        methods={controllerMethods}
        isHideSelectAll={isHideSelectAll}
        isDense={Boolean(margin)}
        isWithClearSelection={isWithClearSelection}
      />
    );
  }, [
    selectedOptions,
    maxSelections,
    label,
    filterValue,
    filterRef.current,
    isOnlyOne,
    isSearchable,
    isHideSelectAll,
    isControllerDisabled,
    controllerMethods,
    isWithClearSelection,
  ]);

  return (
    <Menu
      TransitionProps={{
        timeout: 100,
        onEntered: onMenuEntered,
        onExit: onMenuExited,
      }}
      slotProps={{
        paper: {
          style: {
            width: menuConfig.MENU_WIDTH,
            background: 'none',
            boxShadow:
              'rgba(14, 30, 37, 0.12) 0px 2px 4px 0px, rgba(14, 30, 37, 0.32) 0px 2px 16px 0px',
            ...menuPaperStyle,
          },
        },
      }}
      MenuListProps={{
        style: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={
        popperPosition ?? {
          vertical: 'top',
          horizontal: 'center',
          ...menuProps?.transformOrigin,
        }
      }
      disablePortal={disablePortal}
      anchorEl={refElement.current}
      open={isOpen}
      onClose={onSelectClose}
      sx={{
        zIndex: 1600,
        ...menuStyle,
      }}
      {...menuProps}
    >
      <Box component="span">
        {!isBottomControls ? SelectController : null}
        <List
          options={sortedOptions}
          selectedOptions={selectedOptions}
          filteredOptions={showingOptions}
          filterValue={filterValue}
          menuConfig={menuConfig}
          handleItemChange={handleItemChange}
          isDense={Boolean(margin)}
          isOnlyOne={isOnlyOne}
          isWithRowSelectInputs
          isWithController={isSearchable}
          isMenuFinishedOpen={isMenuOpen}
          style={style}
        />
        {isBottomControls ? SelectController : null}
      </Box>
    </Menu>
  );
};

export default CustomSelect;
export type { CustomSelectProps, Option };
